/* eslint-disable react-hooks/static-components */
/* eslint-disable react-hooks/set-state-in-effect */
import React, { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Overflow,
  OverflowItem,
  Tab,
  TabList,
  type SelectTabData,
  type SelectTabEvent,
  useOverflowMenu,
} from "@fluentui/react-components";
import {
  PersonRegular,
  GridRegular,
  HistoryRegular,
  AppsListRegular,
  GridDotsRegular,
  GridKanbanRegular,
  CalendarRtlRegular,
  TransmissionRegular,
  MoreHorizontalRegular,
} from "@fluentui/react-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { ROLES } from "../../constants/roles";
import Grunddaten from "./grunddaten";
import History from "./history";
import { getCurrentRole } from "../../utils/role";
import Qualifikationsprofil from "./qualifikationsprofil";
import { DataService } from "../../services/dataService";
import type { IProjektItem } from "../../models/IProjektItem";
import type { IDokumentItem } from "../../models/IDokumentItem";
import Projektplan from "./projektplan";

const DetailView: React.FC = () => {
  const [roles, setRoles] = useState<string[]>(getCurrentRole());
  const [canEditGrunddaten, setCanEditGrunddaten] = useState<boolean>(() =>
    getCurrentRole().some(
      (role) => role === "Admin" || role === "Freigeber*in",
    ),
  );
  const [canEditQualifikationsprofil, setCanEditQualifikationsprofil] =
    useState<boolean>(() =>
      getCurrentRole().some(
        (role) =>
          role === "Admin" ||
          role === "Entwickler*in" ||
          role === "Freigeber*in",
      ),
    );
  const [canEditProjektplan, setCanEditProjektplan] = useState<boolean>(() =>
    getCurrentRole().some(
      (role) =>
        role === "Admin" || role === "Entwickler*in" || role === "Freigeber*in",
    ),
  );
  const location = useLocation();
  const navigate = useNavigate();
  const projektId: number | undefined = location.state?.projektId;
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(
    () => (accountInfo ? new DataService(instance, accountInfo) : null),
    [instance, accountInfo],
  );
  const [projekt, setProjekt] = useState<IProjektItem | null>(null);
  const [dokumente, setDokumente] = useState<IDokumentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const requestScope = React.useRef<{
    active: boolean;
    projektId: number;
    dataService: DataService;
    projektRequest: number;
    dokumenteRequest: number;
  } | null>(null);

  const tabs = [
    {
      value: "grunddaten",
      label: "Grunddaten & relevante Personen",
      icon: <PersonRegular />,
    },
    {
      value: "qualifikationsprofil",
      label: "Qualifikationsprofil",
      icon: <TransmissionRegular />,
    },
    {
      value: "projektplan",
      label: "Projektplan",
      icon: <CalendarRtlRegular />,
    },
    {
      value: "kachelmatrix",
      label: "Kachelmatrix",
      icon: <GridDotsRegular />,
    },
    {
      value: "curriculummatrix",
      label: "Curriculummatrix",
      icon: <GridRegular />,
    },
    {
      value: "konkordanzmatrix",
      label: "Konkordanzmatrix",
      icon: <GridKanbanRegular />,
    },
    {
      value: "kompetenzraster",
      label: "Kompetenzraster",
      icon: <AppsListRegular />,
    },
    {
      value: "history",
      label: "History / Protokoll",
      icon: <HistoryRegular />,
    },
  ];

  useEffect(() => {
    const handleRoleChange = () => {
      const newRoles = getCurrentRole();
      setRoles(newRoles);
      setCanEditGrunddaten(
        newRoles.some((role) => role === "Admin" || role === "Freigeber*in"),
      );
      setCanEditQualifikationsprofil(
        newRoles.some(
          (role) =>
            role === "Admin" ||
            role === "Entwickler*in" ||
            role === "Freigeber*in",
        ),
      );
      setCanEditProjektplan(
        newRoles.some(
          (role) =>
            role === "Admin" ||
            role === "Entwickler*in" ||
            role === "Freigeber*in",
        ),
      );
    };

    window.addEventListener("role-changed", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("role-changed", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, [roles]);

  const hasRole = (roleName: string) =>
    roles.some((r) => r.toLowerCase() === roleName.toLowerCase());

  const hasPermission = ROLES.some((allowedRole) => hasRole(allowedRole));

  const defaultTab = location.state?.defaultTab || "grunddaten";
  const [selectedTab, setSelectedTab] = useState<string>(defaultTab);

  useEffect(() => {
    if (location.state?.defaultTab) {
      setSelectedTab(location.state.defaultTab);
      return;
    }

    const params = new URLSearchParams(location.search);
    const taskGuid = params.get("taskguid");
    const source = params.get("source");

    if (!taskGuid || !source || !dataService || !hasPermission) return;

    const validTabs = [
      "grunddaten",
      "qualifikationsprofil",
      "projektplan",
      "kachelmatrix",
      "curriculummatrix",
      "konkordanzmatrix",
      "kompetenzraster",
      "history",
    ];
    const selectedTabFromUrl = validTabs.includes(source)
      ? source
      : "grunddaten";

    const loadProjectByTaskGuid = async () => {
      try {
        const project = await dataService.getProjektWithTaskGuid(taskGuid);
        navigate("/detail", {
          state: {
            projektId: project.itemId,
            projektName: project.bezeichnung ?? "",
            qualifikationsprofil: project.qualifikationsprofil ?? "",
            defaultTab: selectedTabFromUrl,
          },
        });
        setSelectedTab(selectedTabFromUrl);
      } catch (error) {
        console.error("Failed to load project from URL taskguid:", error);
      }
    };

    void loadProjectByTaskGuid();
  }, [dataService, hasPermission, location.search, location.state, navigate]);

  const reloadProjekt = React.useCallback(async () => {
    const scope = requestScope.current;
    if (
      !scope?.active ||
      scope.projektId !== projektId ||
      scope.dataService !== dataService
    ) {
      throw new Error("Projektdaten können derzeit nicht geladen werden.");
    }

    const request = ++scope.projektRequest;
    const response = await scope.dataService.getProjekt(scope.projektId);
    if (scope.active && request === scope.projektRequest) {
      setProjekt(response);
    }
  }, [dataService, projektId]);

  const reloadDokumente = React.useCallback(async () => {
    const scope = requestScope.current;
    if (
      !scope?.active ||
      scope.projektId !== projektId ||
      scope.dataService !== dataService
    ) {
      throw new Error("Dokumente können derzeit nicht geladen werden.");
    }

    const request = ++scope.dokumenteRequest;
    const response = await scope.dataService.getProjektDokumente(
      scope.projektId,
    );
    if (scope.active && request === scope.dokumenteRequest) {
      setDokumente(response);
    }
  }, [dataService, projektId]);

  useEffect(() => {
    setProjekt(null);
    setDokumente([]);
    setLoadError("");
    setIsLoading(true);
    if (!dataService || projektId === undefined || !hasPermission) return;

    const scope = {
      active: true,
      projektId,
      dataService,
      projektRequest: 0,
      dokumenteRequest: 0,
    };
    requestScope.current = scope;
    const loadData = async () => {
      try {
        await Promise.all([reloadProjekt(), reloadDokumente()]);
      } catch (error) {
        console.error("Failed to load project detail data:", error);
        if (scope.active) {
          setLoadError(
            "Projektdaten und Dokumente konnten nicht geladen werden.",
          );
        }
      } finally {
        if (scope.active) setIsLoading(false);
      }
    };

    void loadData();
    return () => {
      scope.active = false;
    };
  }, [dataService, projektId, hasPermission, reloadProjekt, reloadDokumente]);

  const onTabSelect = (_event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value as string);
  };

  const TabOverflowMenu: React.FC<{
    tabs: typeof tabs;
    selectedTab: string;
    onSelectTab: (value: string) => void;
  }> = ({ tabs, selectedTab, onSelectTab }) => {
    const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>(
      "detail-tab-overflow",
    );

    if (!isOverflowing) {
      return null;
    }

    return (
      <Menu positioning="below-end">
        <MenuTrigger disableButtonEnhancement>
          <Button
            ref={ref}
            appearance="subtle"
            icon={<MoreHorizontalRegular />}
            aria-label="Mehr Tabs"
            title="Mehr Tabs"
          />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {tabs
              .filter((tab) => tab.value !== selectedTab)
              .map((tab) => (
                <MenuItem
                  key={tab.value}
                  onClick={() => onSelectTab(tab.value)}
                >
                  {tab.label}
                </MenuItem>
              ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  };

  const renderActiveTabContent = () => {
    if (projektId === undefined) {
      return <p>Kein Projekt ausgewählt.</p>;
    }
    if (loadError) {
      return <p role="alert">{loadError}</p>;
    }
    if (isLoading || !projekt || projekt.itemId !== projektId) {
      return <p role="status">Projektdaten werden geladen ...</p>;
    }

    switch (selectedTab) {
      case "qualifikationsprofil":
        return (
          <Qualifikationsprofil
            canEdit={canEditQualifikationsprofil}
            projekt={projekt}
            dokumente={dokumente}
            onProjektUpdated={reloadProjekt}
            onDokumenteUpdated={reloadDokumente}
          />
        );
      case "projektplan":
        return (
          <Projektplan
            canEdit={canEditProjektplan}
            projekt={projekt}
            dokumente={dokumente}
            onProjektUpdated={reloadProjekt}
            onDokumenteUpdated={reloadDokumente}
          />
        );
      case "kachelmatrix":
        return <div className="tabPanel">Kachelmatrix</div>;
      case "curriculummatrix":
        return <div className="tabPanel">Curriculummatrix</div>;
      case "konkordanzmatrix":
        return <div className="tabPanel">Konkordanzmatrix</div>;
      case "kompetenzraster":
        return <div className="tabPanel">Kompetenzraster</div>;
      case "history":
        return <History />;
    }
    return (
      <Grunddaten
        canEdit={canEditGrunddaten}
        projekt={projekt}
        dokumente={dokumente}
        onProjektUpdated={reloadProjekt}
        onDokumenteUpdated={reloadDokumente}
      />
    );
  };

  return (
    <>
      {hasPermission ? (
        <>
          <h2 className="sectionTitle">
            Detailansicht:{" "}
            {projekt && projekt.itemId === projektId
              ? projekt.bezeichnung
              : location.state?.projektName}
          </h2>
          <div className="container">
            <div className="header">
              <Overflow
                overflowAxis="horizontal"
                overflowDirection="end"
                minimumVisible={1}
              >
                <TabList
                  className="detailTabs"
                  selectedValue={selectedTab}
                  onTabSelect={onTabSelect}
                >
                  {tabs.map((tab) => (
                    <OverflowItem
                      key={tab.value}
                      id={tab.value}
                      priority={selectedTab === tab.value ? 2 : 1}
                    >
                      <Tab
                        value={tab.value}
                        icon={tab.icon}
                        className="detailTab"
                      >
                        {tab.label}
                      </Tab>
                    </OverflowItem>
                  ))}
                  <OverflowItem id="tab-overflow-menu" priority={0}>
                    <TabOverflowMenu
                      tabs={tabs}
                      selectedTab={selectedTab}
                      onSelectTab={setSelectedTab}
                    />
                  </OverflowItem>
                </TabList>
              </Overflow>
              <Divider />
            </div>
            <div className="tabContent" key={projektId}>
              {renderActiveTabContent()}
            </div>
          </div>
        </>
      ) : (
        <div>Keine Berechtigung</div>
      )}
    </>
  );
};
export default DetailView;
